import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Slide } from '@mui/material'
import { Outlet } from 'react-router-dom'
import { useSystemNotifications } from '@/features/status'
import { LoginModal, useAuthStatus } from '@/features/user'
import { OnboardingGate } from '@/features/onboarding'
import {
  COLLAPSED_WIDTH,
  DashboardSidebar,
  SIDEBAR_WIDTH,
  SidebarProvider,
} from '@/features/sidebar'
import { useSidebarState } from '@/features/sidebar/hooks'
import { ErrorBoundary } from '@/infra/error'
import { BlockingInbox, SessionDialogManagerProvider } from '@/features/inbox'

export const AppLayout: FC = () => {
  return (
    <SidebarProvider>
      <AppLayoutContent />
    </SidebarProvider>
  )
}

const AppLayoutContent: FC = () => {
  const { isAuthenticated } = useAuthStatus()
  const [showUsernameSelection, setShowUsernameSelection] = useState(false)
  const [isAuthMinimized, setIsAuthMinimized] = useState(false)

  useSystemNotifications()

  const { isPinned } = useSidebarState()

  const shouldShowAuth = !isAuthenticated && !showUsernameSelection && !isAuthMinimized

  const handleAuthClose = useCallback((): void => {
    setShowUsernameSelection(false)
    setIsAuthMinimized(false)
  }, [])

  const handleAuthMinimize = useCallback((): void => {
    setShowUsernameSelection(false)
    setIsAuthMinimized(true)
  }, [])

  const handleAuthRestore = useCallback((): void => {
    setIsAuthMinimized(false)
    setShowUsernameSelection(true)
  }, [])

  const sidebarWidth = isPinned ? SIDEBAR_WIDTH : COLLAPSED_WIDTH

  return (
    <Box sx={layoutContainerSx}>
      <DashboardSidebar />

      <Box component="main" sx={mainContentSx(sidebarWidth)}>
        <SessionDialogManagerProvider>
          <Box
            id="app-content"
            data-testid="app-content"
            sx={contentBlurSx(showUsernameSelection || shouldShowAuth)}
          >
            {!isAuthenticated && isAuthMinimized && (
              <Box sx={authButtonContainerSx}>
                <Slide
                  direction="left"
                  in={isAuthMinimized && !isAuthenticated}
                  timeout={{ enter: 300, exit: 200 }}
                  mountOnEnter
                  unmountOnExit
                >
                  <IconButton onClick={handleAuthRestore} sx={authButtonSx} size="small">
                    Sign In
                  </IconButton>
                </Slide>
              </Box>
            )}

            <Outlet />
          </Box>

          <ErrorBoundary>
            <BlockingInbox />
          </ErrorBoundary>
        </SessionDialogManagerProvider>

        <LoginModal
          open={showUsernameSelection || shouldShowAuth}
          onClose={handleAuthClose}
          onMinimize={handleAuthMinimize}
        />

        <OnboardingGate />
      </Box>
    </Box>
  )
}

const layoutContainerSx: SxProps<Theme> = {
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: 'background.default',
}

const mainContentSx = (sidebarWidth: number): SxProps<Theme> => ({
  flexGrow: 1,
  marginLeft: `${sidebarWidth}px`,
  transition: 'margin-left 250ms ease-in-out',
  position: 'relative',
})

const contentBlurSx = (shouldBlur: boolean): SxProps<Theme> => ({
  filter: shouldBlur ? 'blur(2px)' : 'none',
  transition: 'filter 300ms ease-in-out',
  pointerEvents: shouldBlur ? 'none' : 'auto',
})

const authButtonContainerSx: SxProps<Theme> = {
  position: 'absolute',
  top: 24,
  right: 24,
  zIndex: 1000,
}

const authButtonSx: SxProps<Theme> = {
  backgroundColor: 'action.hover',
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'divider',
  '&:hover': {
    backgroundColor: 'action.selected',
    borderColor: 'text.disabled',
    color: 'text.primary',
  },
  fontSize: '0.75rem',
  minWidth: 60,
  height: 32,
  borderRadius: 1,
  transition: 'all 200ms ease-in-out',
  fontWeight: 500,
}
